import { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getDeviceContacts } from '../../../api/connections';
import type { DeviceContact } from '../../../types/connections';

interface TerminalSchemaDeviceNodeData {
  deviceName: string;
  deviceType: string;
  specName: string;
  description: string;
  deviceInstanceId: number;
  specificationId?: number;
  contacts: DeviceContact[];
  onRemoveDevice?: (layoutId: number) => void;
  onEditInstance?: (layoutId: number, deviceInstanceId: number) => void;
  layoutId: number;
  isReserve?: boolean; // true если элемент резервный
  isConnecting?: boolean;
  schemaId?: number; // Schema ID for filtering contacts
}

interface TerminalSchemaDeviceNodeProps {
  data: TerminalSchemaDeviceNodeData;
}

/**
 * TERMINAL SCHEMA: Specialized node component for terminal schemas
 * Features:
 * - Compact terminal block visualization
 * - Bottom-only contact connections (simplified)
 * - Terminal-specific color scheme (orange/amber)
 * - Position-based terminal layouts
 */
export default function TerminalSchemaDeviceNode({ data }: TerminalSchemaDeviceNodeProps) {
  const {
    deviceName,
    deviceType,
    specName,
    description,
    deviceInstanceId,
    contacts: initialContacts = [],
    isReserve = false,
    isConnecting = false
  } = data;

  // Debug log for connection highlighting
  console.log(`🔍 TerminalSchemaDeviceNode ${deviceName}: isConnecting = ${isConnecting}`);





  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [loading, setLoading] = useState(false);

  const isCable = deviceType === 'cable' || deviceType === 'Кабель';

  // Color schemes based on reserve status
  const cableColors = {
    border: isReserve ? 'border-gray-500' : 'border-green-500',
    bg: isReserve ? 'bg-gray-50' : 'bg-green-50',
    bgSecondary: isReserve ? 'bg-gray-100' : 'bg-green-100',
    borderSecondary: isReserve ? 'border-gray-200' : 'border-green-200',
    text: isReserve ? 'text-gray-700' : 'text-green-700',
    textPrimary: isReserve ? 'text-gray-800' : 'text-green-800',
    handleBg: isReserve ? 'bg-gray-100' : 'bg-green-100',
    handleBorder: isReserve ? 'border-gray-500' : 'border-green-500'
  };

  const deviceColors = {
    border: isReserve ? 'border-gray-500' : 'border-orange-500',
    bg: isReserve ? 'bg-gray-50' : 'bg-orange-50',
    borderSecondary: isReserve ? 'border-gray-200' : 'border-orange-200',
    text: isReserve ? 'text-gray-700' : 'text-orange-700',
    textSecondary: isReserve ? 'text-gray-600' : 'text-orange-600',
    handleBg: isReserve ? 'bg-gray-100' : 'bg-orange-100',
    handleBorder: isReserve ? 'border-gray-500' : 'border-orange-500',
    handleBorderSecondary: isReserve ? 'border-gray-600' : 'border-orange-600'
  };

  // Sync contacts state with initialContacts prop for devices and cables
  useEffect(() => {
    if (initialContacts && initialContacts.length > 0) {
      setContacts(initialContacts);
    }
  }, [initialContacts, deviceName]);

  // Load cable contacts asynchronously for cable instances (only if not already provided)
  useEffect(() => {
    if (isCable && deviceInstanceId && (!initialContacts || initialContacts.length === 0)) {
      const loadCableContacts = async () => {
        try {
          setLoading(true);
          // Load contacts with schema_id filter (priority) or terminal schema type (fallback)
          const allCableContacts = data.specificationId 
            ? await getDeviceContacts(data.specificationId, 'terminal', data.schemaId) 
            : [];
          setContacts(allCableContacts);
        } catch (error) {
          console.error('Error loading cable contacts:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCableContacts();
    }
  }, [isCable, deviceInstanceId, deviceName, data.specificationId, data.schemaId, initialContacts]);

  // TERMINAL SCHEMA: Cable rendering with top and bottom contacts
  if (isCable) {
    // Filter and sort contacts by wire numbers, take only first 3 for terminal schema
    const topContacts = contacts
      .filter(c => String(c.contact_number).includes('-TOP'))
      .sort((a, b) => {
        const numA = parseInt(String(a.contact_number).split('-')[0]);
        const numB = parseInt(String(b.contact_number).split('-')[0]);
        return numA - numB;
      })
      .slice(0, 3); // Take only first 3 contacts for terminal schema

    const bottomContacts = contacts
      .filter(c => String(c.contact_number).includes('-BOTTOM'))
      .sort((a, b) => {
        const numA = parseInt(String(a.contact_number).split('-')[0]);
        const numB = parseInt(String(b.contact_number).split('-')[0]);
        return numA - numB;
      })
      .slice(0, 3); // Take only first 3 contacts for terminal schema

    // Debug: log contacts for troubleshooting
    if (deviceInstanceId === 2627) {
      console.log('🔍 Cable 2627 contacts:', { 
        all: contacts.length, 
        top: topContacts.length, 
        bottom: bottomContacts.length,
        topContacts: topContacts.map(c => c.contact_number),
        bottomContacts: bottomContacts.map(c => c.contact_number)
      });
    }

    const contactsCount = topContacts.length;
    const cableWidth = contactsCount <= 4 ? 90 : Math.max(90, contactsCount * 18 + 20);

    if (loading) {
      return (
        <div className={`bg-white border-2 ${cableColors.border} rounded shadow-lg p-2`} style={{ width: cableWidth, minHeight: '100px' }}>
          <div className="text-center text-gray-500 text-xs">Loading cable...</div>
        </div>
      );
    }

    return (
      <div 
        className={`bg-white border-2 ${cableColors.border} rounded shadow-lg select-none`}
        style={{ width: cableWidth, minHeight: '100px' }}
      >
        {/* TOP contacts with handles - только TARGET для кабелей сверху */}
        <div className={`flex justify-around items-center px-1 py-1 border-b ${cableColors.borderSecondary}`}>
          {topContacts.map((contact) => (
            <div key={`${contact.contact_number}-top`} className="flex flex-col items-center relative">
              <div className={`w-3 h-3 rounded-full border-2 ${cableColors.handleBorder} ${cableColors.handleBg} relative`}>
                              <Handle
                type="target"
                position={Position.Top}
                id={`${contact.id}-target`}
                className={`schema-target-handle !w-3 !h-3 !border-2 !border-blue-500 ${
                  isConnecting ? 'highlighted' : ''
                }`}
                style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                isConnectable={true}
              />
              </div>
            </div>
          ))}
        </div>

        {/* Wire colors section (top) */}
        <div className={`px-2 py-1 border-b ${cableColors.borderSecondary} ${cableColors.bg}`}>
          <div className="flex justify-around" style={{ fontSize: '8px' }}>
            {topContacts.map((contact) => (
              <div key={`color-${contact.contact_number}`} className={`text-center font-bold ${cableColors.text}`}>
                {contact.color_code || String(contact.contact_number).replace('-TOP', '')}
              </div>
            ))}
          </div>
        </div>

        {/* Position label */}
        <div className={`px-2 py-1 text-center border-b ${cableColors.borderSecondary}`}>
          <div className={`font-bold ${cableColors.textPrimary}`} style={{ fontSize: '11px' }}>
                                {deviceName || "—"}
          </div>
        </div>

        {/* Cable specification */}
        <div className={`px-2 py-1 text-center border-b ${cableColors.borderSecondary} ${cableColors.bgSecondary}`}>
          <div className={`font-bold ${cableColors.text}`} style={{ fontSize: '10px' }}>
            {specName}
          </div>
        </div>

        {/* Wire colors section (bottom) - подписи над нижними контактами */}
        <div className={`px-2 py-1 border-b ${cableColors.borderSecondary} ${cableColors.bg}`}>
          <div className="flex justify-around" style={{ fontSize: '8px' }}>
            {bottomContacts.map((contact) => (
              <div key={`color-bottom-${contact.contact_number}`} className={`text-center font-bold ${cableColors.text}`}>
                {contact.color_code || String(contact.contact_number).replace('-BOTTOM', '')}
              </div>
            ))}
          </div>
        </div>

        {/* BOTTOM contacts with handles - только SOURCE для кабелей снизу */}
        <div className={`flex justify-around items-center px-1 py-1 border-t ${cableColors.borderSecondary}`}>
          {bottomContacts.map((contact) => (
            <div key={`${contact.contact_number}-bottom`} className="flex flex-col items-center relative">
              <div className={`w-3 h-3 rounded-full border-2 ${cableColors.handleBorder} ${cableColors.handleBg} relative`}>
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={`${contact.id}-source`}
                  className={`schema-source-handle !w-3 !h-3 !bg-white !border-2 !border-blue-500 ${
                    isConnecting ? 'highlighted' : ''
                  }`}
                  style={{ bottom: '50%', transform: 'translate(-50%, 50%)' }}
                  isConnectable={true}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // TERMINAL SCHEMA: Device/Terminal block rendering
  const deviceWidth = Math.max(60, contacts.length * 15 + 15); // Компактный дизайн

  return (
    <div 
      className={`bg-white border-2 ${deviceColors.border} rounded shadow-lg select-none`}
      style={{ width: deviceWidth, minHeight: '70px' }}
    >
      {/* Terminal contacts - BOTTOM only for terminal schemas */}
      <div className={`px-1 py-1 text-center border-b ${deviceColors.borderSecondary}`}>
        <div className={`font-bold ${deviceColors.text}`} style={{ fontSize: '9px' }}>
          {description && description.trim() ? description : specName || deviceName}
        </div>
      </div>

      {/* BOTTOM contacts for terminal connection - только TARGET для устройств снизу */}
      <div className="flex justify-around items-center px-1 py-1 relative">
        {contacts.map((contact) => (
          <div key={contact.id} className="flex flex-col items-center relative">
            <div className="text-xs font-medium text-gray-700 mb-0.5" style={{ fontSize: '7px' }}>
              {contact.contact_label || `${contact.contact_number}`}
            </div>
            <div className={`w-2.5 h-2.5 rounded-full border-2 ${deviceColors.handleBorder} ${deviceColors.handleBg} relative`}>
              <Handle
                type="target"
                position={Position.Bottom}
                id={`${contact.id}-target`}
                className={`schema-target-handle !w-2.5 !h-2.5 !border-2 !border-blue-500 ${
                  isConnecting ? 'highlighted' : ''
                }`}
                style={{ bottom: '50%', transform: 'translate(-50%, 50%)' }}
                isConnectable={true}
              />
            </div>
          </div>
        ))}
      </div>

      {/* TERMINAL SCHEMA: Simplified terminal info */}
      <div className={`px-1 py-0.5 text-center border-t ${deviceColors.borderSecondary} ${deviceColors.bg}`}>
        <div className={`${deviceColors.textSecondary}`} style={{ fontSize: '7px' }}>
          Terminal Block
        </div>
      </div>
    </div>
  );
} 